// ======================================================
// 📁 src/context/AuthContext.tsx — Platinum PRO 2025 (Optimisé & Aligné Backend)
// ======================================================

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import api, {
  setAccessToken as setAxiosAccessToken,
  setClientAccessToken as setAxiosClientToken,
} from "../lib/axios";

// ======================================================
// 🧠 Typages
// ======================================================

export type UserRole =
  | "admin"
  | "superviseur"
  | "agent"
  | "planificateur"
  | "chef"
  | "portier"
  | "recouvrement"
  | "mairie"
  | "finance"
  | "client";

export interface User {
  id: number;
  nom: string;
  email?: string;
  role: UserRole;
}

export interface AuthContextType {
  user: User | null;
  /** Jeton staff (admin, planif., etc.) — jamais utilisé pour le portail client. */
  accessToken: string | null;
  /** Jeton B2C portail client (rôle `client`). */
  clientAccessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, accessToken: string) => void; // ✅ refreshToken supprimé
  logout: (showToast?: boolean) => Promise<void>;
  refreshUser: () => Promise<void>;
}

// ======================================================
// 🧩 Création du contexte
// ======================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ======================================================
// ⚙️ Provider principal
// ======================================================

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [clientAccessToken, setClientAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // ======================================================
  // 🚪 Déconnexion
  // ======================================================

  const handleLogout = useCallback(
    async (showToast: boolean = true): Promise<void> => {
      try {
        await api.post("/auth/logout", {}, { withCredentials: true });
      } catch {
        // Session déjà invalide → ignorer
      } finally {
        setUser(null);
        setAccessToken(null);
        setClientAccessToken(null);
        setAxiosAccessToken(null);
        setAxiosClientToken(null);

        localStorage.removeItem("user");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("clientAccessToken");
        localStorage.removeItem("role");
        localStorage.removeItem("clientPortalSession");

        if (showToast) toast.info("✅ Déconnecté avec succès");
      }
    },
    []
  );

  // ======================================================
  // 🔄 Rafraîchir utilisateur
  // ======================================================

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<User>("/auth/me", {
        withCredentials: true,
      });

      if (!data?.id) throw new Error("Utilisateur invalide");

      setUser(data);
      localStorage.setItem("user", JSON.stringify(data));
      localStorage.setItem("role", data.role);
    } catch (err) {
      console.error("❌ Refresh user failed:", err);
      await handleLogout(false);
    }
  }, [handleLogout]);

  // ======================================================
  // 🚀 Hydratation initiale
  // ======================================================

  useEffect(() => {
    const initSession = async () => {
      try {
        const storedUserRaw = localStorage.getItem("user");
        if (!storedUserRaw) {
          setIsLoading(false);
          return;
        }

        const parsedUser = JSON.parse(storedUserRaw) as User;
        const isClient = String(parsedUser.role).toLowerCase() === "client";

        if (isClient) {
          const legacy = localStorage.getItem("accessToken");
          const dedicated = localStorage.getItem("clientAccessToken");
          if (legacy && !dedicated) {
            localStorage.setItem("clientAccessToken", legacy);
            localStorage.removeItem("accessToken");
            setAxiosClientToken(legacy);
          }
        }

        const token = isClient
          ? localStorage.getItem("clientAccessToken")
          : localStorage.getItem("accessToken");

        if (token) {
          setUser(parsedUser);
          if (isClient) {
            setClientAccessToken(token);
            setAxiosClientToken(token);
            setAccessToken(null);
            setAxiosAccessToken(null);
          } else {
            setAccessToken(token);
            setAxiosAccessToken(token);
            setClientAccessToken(null);
            setAxiosClientToken(null);
          }
          await refreshUser();
        }
      } catch (err) {
        console.warn("⚠️ Session invalide:", err);
        await handleLogout(false);
      } finally {
        setIsLoading(false);
      }
    };

    initSession();
  }, [refreshUser, handleLogout]);

  // ======================================================
  // 🔑 Login
  // ======================================================

  const handleLogin = (
    userData: User,
    newAccessToken: string
  ): void => {
    if (!newAccessToken) {
      toast.error("❌ Jeton manquant.");
      return;
    }

    setUser(userData);
    const isClient = String(userData.role).toLowerCase() === "client";

    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("role", userData.role);

    if (isClient) {
      setAccessToken(null);
      setAxiosAccessToken(null);
      localStorage.removeItem("accessToken");
      setClientAccessToken(newAccessToken);
      setAxiosClientToken(newAccessToken);
    } else {
      setClientAccessToken(null);
      setAxiosClientToken(null);
      localStorage.removeItem("clientAccessToken");
      setAccessToken(newAccessToken);
      setAxiosAccessToken(newAccessToken);
    }
  };

  // ======================================================
  // 🔔 Écoute expiration session (déclenché par axios)
  // ======================================================

  useEffect(() => {
    const handleSessionExpired = async () => {
      await handleLogout(false);
    };

    window.addEventListener("sessionExpired", handleSessionExpired);
    return () =>
      window.removeEventListener("sessionExpired", handleSessionExpired);
  }, [handleLogout]);

  // ======================================================
  // 🧱 Fournisseur
  // ======================================================

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        clientAccessToken,
        isAuthenticated:
          !!user && (!!accessToken || !!clientAccessToken),
        isLoading,
        login: handleLogin,
        logout: handleLogout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ======================================================
// ⚙️ Hook useAuth
// ======================================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context)
    throw new Error("useAuth doit être utilisé dans <AuthProvider>");
  return context;
};